import { useQuery } from "@tanstack/react-query";
import { Seo } from "@/components/Seo";
import { JsonLd, faqSchema } from "@/components/StructuredData";
import { Icon } from "@/components/Icon";
import { KbArticles } from "@/components/KbArticles";
import { fetchKbArticles } from "@/lib/kb-articles";
import { FAQ } from "@/lib/faq";
import { useContactEmail } from "@/lib/use-app-config";

const TITLE = "Întrebări — Asistent Auto";
const DESCRIPTION =
  "Răspunsuri scurte la întrebările despre aplicație și articolele complete din secțiunea „Informații utile” a aplicației.";

export default function IntrebariPage() {
  const { data: articles = [] } = useQuery({
    queryKey: ["kb-articles"],
    queryFn: fetchKbArticles,
    staleTime: 5 * 60 * 1000,
  });
  const contactEmail = useContactEmail();

  return (
    <>
      <Seo title={TITLE} description={DESCRIPTION} path="/intrebari" />
      {/* Built from the static FAQ array, so it survives prerendering —
          the Firestore-backed KbArticles below deliberately stay out. */}
      <JsonLd data={faqSchema(FAQ)} />
      <header className="page-hero">
        <div className="shell">
          <div className="section-eyebrow">
            Întrebări frecvente · Informații utile
          </div>
          <h1>
            Câteva lucruri pe care<br />
            <em>ne place să le clarificăm.</em>
          </h1>
          <p>
            Răspunsuri scurte la întrebările despre aplicație. Mai jos,
            articolele complete pe care le citești și în secțiunea „Informații
            utile” din aplicație — sincronizate live din baza noastră.
          </p>
        </div>
      </header>

      <section className="section section--tight">
        <div className="shell" style={{ maxWidth: 880, paddingTop: 0 }}>
          <div className="faq">
            {FAQ.map((item, i) => (
              <details key={item.q} open={i === 0}>
                <summary>{item.q}</summary>
                <p dangerouslySetInnerHTML={{ __html: item.a }} />
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--tight" id="informatii-utile">
        <div className="shell">
          <div className="kb-header">
            <div>
              <div className="section-eyebrow">
                Informații utile din aplicație
              </div>
              <h2>
                Articole pe care le scriem când <em>se mișcă piața.</em>
              </h2>
              <p>
                Aceleași texte pe care le citești în aplicație, la „Informații
                utile”. Le actualizăm când ASF publică ceva, când un asigurător
                se schimbă, sau când apare o lege nouă.
              </p>
            </div>
            <span className="kb-live">
              <span className="dot" />
              SINCRONIZAT LIVE · {articles.length} ARTICOLE
            </span>
          </div>

          <KbArticles articles={articles} />

          <div className="resources-strip">
            <a
              href="https://portal.asfromania.ro/portal_ext/login.xhtml"
              target="_blank"
              rel="noopener noreferrer"
              className="resource-card"
            >
              <div className="resource-head">
                <div className="resource-icon">
                  <Icon name="gavel" />
                </div>
                <div>
                  <div className="resource-eyebrow">Autoritate</div>
                  <div className="resource-title">Petiții ASF România</div>
                </div>
                <Icon name="arrow_outward" className="resource-arrow" />
              </div>
              <p>
                Autoritatea de Supraveghere Financiară. Aici depui plângere
                dacă un asigurător refuză daună, întârzie despăgubirea peste
                30 de zile, sau încalcă termenii poliței. Răspuns oficial în
                30 de zile.
              </p>
            </a>
            <a
              href="https://www.fgaromania.ro"
              target="_blank"
              rel="noopener noreferrer"
              className="resource-card"
            >
              <div className="resource-head">
                <div className="resource-icon">
                  <Icon name="account_balance" />
                </div>
                <div>
                  <div className="resource-eyebrow">Despăgubiri</div>
                  <div className="resource-title">
                    FGA — Fondul de Garantare
                  </div>
                </div>
                <Icon name="arrow_outward" className="resource-arrow" />
              </div>
              <p>
                Fondul de Garantare a Asiguraților. Aici recuperezi banii dacă
                asigurătorul tău intră în faliment (City Insurance, Euroins).
                Cerere standardizată, termen 90 de zile de la eveniment.
              </p>
            </a>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="shell">
          <div className="cta-strip">
            <div style={{ position: "relative", zIndex: 1 }}>
              <h2>
                Tot ai întrebări? <em>Vorbește cu un om.</em>
              </h2>
              <p>
                Articolele acoperă scenariile comune. Pentru orice altceva —
                falimente, dosar de daună, polițe-fantomă — scrie sau sună.
                Răspundem în aceeași zi în zilele lucrătoare.
              </p>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                <a
                  href={`mailto:${contactEmail}`}
                  className="btn btn--ghost-light btn--lg"
                >
                  <Icon name="mail" />
                  Scrie-ne pe email
                </a>
              </div>
            </div>
            <div />
          </div>
        </div>
      </section>
    </>
  );
}
